import { c as _c } from "react/compiler-runtime";
import { useContext, useRef, useState } from 'react';
import useSWR from 'swr';
import { SeekPagination } from '../../../../../../../typings/API.interface';
import { Event } from '../../../../../../../typings/Event.interface';
import { IgnoredEvent } from '../../../../../../../typings/IgnoredEvent.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { Request } from '../../../../../../../typings/Request.interface';
export interface UseRequestListResult {
  entries: (Event | IgnoredEvent)[];
  fetched: boolean;
  validating: boolean;
  revalidate: (events?: Event[]) => void;
  count: number;
  has_next: boolean;
  has_prev: boolean;
  next: () => void;
  prev: () => void;
}

/**
 * The request row carries its fan-out counts, so a request with no events (or
 * no ignored events) never needs the corresponding list call. Missing counts
 * (older payloads) fall back to fetching.
 */
const hasEvents = (request?: Request): boolean => {
  if (!request) return false;
  if (request.events_count == null && request.cli_events_count == null) return true;
  return (request.events_count ?? 0) + (request.cli_events_count ?? 0) > 0;
};
const hasIgnoredEvents = (request?: Request): boolean => {
  if (!request) return false;
  if (request.ignored_count == null) return true;
  return request.ignored_count > 0;
};
const useRequestEntriesList = (request, t0) => {
  const $ = _c(38);
  const paging_limit = t0 === undefined ? 10 : t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t1;
  if ($[0] !== paging_limit) {
    t1 = {
      limit: paging_limit
    };
    $[0] = paging_limit;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [events_paging_params, setEventsPagingParams] = useState(t1);
  const cached_cursors = useRef(undefined);
  let t2;
  if ($[2] !== paging_limit) {
    t2 = {
      limit: paging_limit
    };
    $[2] = paging_limit;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [ignored_events_paging_params, setIgnoredEventsPagingParams] = useState(t2);
  const request_id = request?.id;
  let t3;
  if ($[4] !== request) {
    t3 = hasEvents(request);
    $[4] = request;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const has_events = t3;
  const has_ignored_events = hasIgnoredEvents(request);
  const fetch_events = has_events && !ignored_events_paging_params.next && events_paging_params.next !== "$END$";
  let t4;
  if ($[6] !== events_paging_params || $[7] !== fetch_events || $[8] !== request_id) {
    t4 = request_id && fetch_events && APIMethodKeys.requests.listEvents(request_id, {
      ...events_paging_params
    });
    $[6] = events_paging_params;
    $[7] = fetch_events;
    $[8] = request_id;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== HookdeckAPI.requests || $[11] !== events_paging_params || $[12] !== request_id) {
    t5 = () => HookdeckAPI.requests.listEvents(request_id, {
      ...events_paging_params
    });
    $[10] = HookdeckAPI.requests;
    $[11] = events_paging_params;
    $[12] = request_id;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const {
    data: events,
    isValidating: events_validating,
    mutate: mutateEvents
  } = useSWR(t4, t5);
  const fetch_ignored_events = has_ignored_events && (events && events?.count < paging_limit || !fetch_events);
  const ignored_events_limit = fetch_ignored_events && paging_limit - (events?.count || 0);
  let t6;
  if ($[14] !== HookdeckAPI.requests || $[15] !== ignored_events_limit || $[16] !== ignored_events_paging_params || $[17] !== request_id) {
    t6 = () => HookdeckAPI.requests.listIgnoredEvents(request_id, {
      ...ignored_events_paging_params,
      limit: ignored_events_limit
    });
    $[14] = HookdeckAPI.requests;
    $[15] = ignored_events_limit;
    $[16] = ignored_events_paging_params;
    $[17] = request_id;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const {
    data: ignored_events,
    isValidating: ignored_events_validating,
    mutate: mutateIgnoredEvents
  } = useSWR(request_id && fetch_ignored_events && APIMethodKeys.requests.listIgnoredEvents(request_id, {
    ...ignored_events_paging_params,
    limit: ignored_events_limit
  }), t6);
  let t7;
  if ($[19] !== mutateEvents || $[20] !== mutateIgnoredEvents) {
    t7 = events_0 => {
      if (events_0) {
        mutateEvents(data => data && events_0 ? {
          ...data,
          models: [...data.models, ...events_0]
        } : undefined, false);
      } else {
        mutateEvents();
      }
      mutateIgnoredEvents();
    };
    $[19] = mutateEvents;
    $[20] = mutateIgnoredEvents;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const revalidate = t7;
  const fetched = (!fetch_events || events !== undefined) && (!fetch_ignored_events || ignored_events !== undefined);
  let t8;
  if ($[22] !== events?.models) {
    t8 = events?.models || [];
    $[22] = events?.models;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== ignored_events?.models) {
    t9 = ignored_events?.models || [];
    $[24] = ignored_events?.models;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t8 || $[27] !== t9) {
    t10 = [...t8, ...t9];
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const entries = t10;
  const curriedChangeCursor = dir => () => {
    const events_cursor = {
      next: undefined as string | undefined,
      prev: undefined as string | undefined,
      limit: paging_limit
    };
    const ignored_events_cursor = {
      next: undefined as string | undefined,
      prev: undefined as string | undefined,
      limit: paging_limit
    };
    if (dir === "next") {
      if (!events?.pagination.next && !ignored_events) {
        events_cursor.next = "$END$";
        cached_cursors.current = events_paging_params;
      } else {
        if (events?.pagination.next) {
          events_cursor.next = events.pagination.next;
        } else {
          if (ignored_events?.pagination.next) {
            ignored_events_cursor.next = ignored_events.pagination.next;
          }
        }
      }
    } else {
      if (dir === "prev") {
        if (!ignored_events?.pagination.prev && !events) {
          events_cursor.next = cached_cursors.current?.next;
        } else {
          if (ignored_events?.pagination.prev) {
            ignored_events_cursor.prev = ignored_events.pagination.prev;
          } else {
            if (events?.pagination.prev) {
              events_cursor.prev = events.pagination.prev;
            }
          }
        }
      }
    }
    setEventsPagingParams(events_cursor);
    setIgnoredEventsPagingParams(ignored_events_cursor);
  };
  const t11 = events_validating || ignored_events_validating;
  const t12 = entries.length;
  const t13 = !!(events?.pagination?.next || ignored_events?.pagination?.next || has_ignored_events && !ignored_events);
  const t14 = !!(events?.pagination?.prev || ignored_events?.pagination?.prev || has_events && !events);
  const t15 = curriedChangeCursor("next");
  const t16 = curriedChangeCursor("prev");
  let t17;
  if ($[29] !== entries || $[30] !== fetched || $[31] !== revalidate || $[32] !== t11 || $[33] !== t13 || $[34] !== t14 || $[35] !== t15 || $[36] !== t16) {
    t17 = {
      entries,
      fetched,
      validating: t11,
      revalidate,
      count: t12,
      has_next: t13,
      has_prev: t14,
      next: t15,
      prev: t16
    };
    $[29] = entries;
    $[30] = fetched;
    $[31] = revalidate;
    $[32] = t11;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  return t17;
};
export default useRequestEntriesList;